import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface TypewriterProps {
    id: string;
    text: string;
    speed: number;
    chunkSize?: number;
    jitter?: number;
    className?: string;
    markdownComponents?: Record<string, React.ComponentType<any>>;
}

function Typewriter({
    id,
    text,
    speed,
    chunkSize = 1,
    jitter = 0,
    className,
    children,
    markdownComponents,
}: PropsWithChildren<TypewriterProps>) {
    const [displayed, setDisplayed] = useState('');
    const indexRef = useRef(0);
    const cancelledRef = useRef(false);
    const timerRef = useRef<number | null>(null);
    const displayedRef = useRef('');

    useEffect(() => {
        const normalized = text ?? '';

        if (timerRef.current) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        if (displayedRef.current && normalized === displayedRef.current) return;

        const prevDisplayed = displayedRef.current;

        const isPrefixAppend =
            prevDisplayed && normalized.startsWith(prevDisplayed) && prevDisplayed.length < normalized.length;

        if (isPrefixAppend) {
            indexRef.current = prevDisplayed.length;
        } else {
            let commonPrefix = 0;
            const maxCheck = Math.min(prevDisplayed.length, normalized.length);
            while (commonPrefix < maxCheck && prevDisplayed[commonPrefix] === normalized[commonPrefix]) {
                commonPrefix++;
            }

            if (commonPrefix > 0) {
                const kept = prevDisplayed.slice(0, commonPrefix);
                displayedRef.current = kept;
                setDisplayed(kept);
                indexRef.current = commonPrefix;
            } else {
                displayedRef.current = '';
                setDisplayed('');
                indexRef.current = 0;
            }
        }

        cancelledRef.current = false;

        const typeNextChunk = () => {
            if (cancelledRef.current) return;

            const start = indexRef.current;
            const nextChunk = normalized.slice(start, start + chunkSize);

            if (!nextChunk) {
                timerRef.current = window.setTimeout(typeNextChunk, 40);
                return;
            }

            setDisplayed((prev) => {
                const updated = prev + nextChunk;
                displayedRef.current = updated;
                return updated;
            });
            indexRef.current += nextChunk.length;

            if (indexRef.current < normalized.length) {
                const delay = Math.max(10, speed + Math.random() * jitter - jitter / 2);
                timerRef.current = window.setTimeout(typeNextChunk, delay);
            } else {
                timerRef.current = null;
                displayedRef.current = normalized;
            }
        };

        timerRef.current = window.setTimeout(typeNextChunk, 10);

        return () => {
            cancelledRef.current = true;
            if (timerRef.current) {
                window.clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [text, speed, chunkSize, jitter]);

    const defaultMarkdownComponents = {
        p: (props: React.HTMLProps<HTMLParagraphElement>) => <p className="m-0" {...props} />,
    };

    const mergedMarkdownComponents = { ...defaultMarkdownComponents, ...(markdownComponents || {}) };

    return (
        <div className={className}>
            <div className="whitespace-normal">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={mergedMarkdownComponents} key={id}>
                    {displayed}
                </ReactMarkdown>
            </div>
            {children}
        </div>
    );
}

export default Typewriter;
